import axios from 'axios';
import env from 'react-dotenv';
import datas from '../Assets/data.json';
import { Crypter } from '../Functions/Security/Crypter';

/**
 * API Écran — PairingInit / PairingComplete / Jeton / File / Signal.
 *
 * Corps = chaîne JSON chiffrée AES ; réponse chiffrée { Value: { StatutCode, Message, Result } }.
 * Le code HTTP peut être 200 malgré un StatutCode fonctionnel : on expose les deux.
 */

const CLE_STORAGE = 'ebcall.cleEcran';

export function lireCle(): string | null {
    const v = localStorage.getItem(CLE_STORAGE)
    // « undefined »/« null » stockés en chaîne par un ancien bug → considérer absent
    return v && v !== 'undefined' && v !== 'null' ? v : null
}
export function enregistrerCle(cle: string) {
    if (cle) localStorage.setItem(CLE_STORAGE, cle)
}
export function effacerCle() {
    localStorage.removeItem(CLE_STORAGE)
}

/** Result peut être un objet ou une chaîne JSON — extraction tolérante. */
export function parseResult(r: any): any {
    if (typeof r === 'string') { try { return JSON.parse(r) } catch { return r } }
    return r
}

/** Clé de l'écran : noms de champ tolérés selon la réponse du backend. */
export function extraireCle(result: any): string | null {
    const o = parseResult(result)
    if (typeof o === 'string') return o || null
    return o?.CleEcran || o?.cleEcran || o?.Cle || o?.ApiKey || null
}

/** Jeton d'accès : « token » possible en minuscules. */
export function extraireToken(result: any): string | null {
    const o = parseResult(result)
    return o?.Token || o?.token || null
}

interface Reponse {
    http: number;
    statut: number;
    message: string;
    result: any;
}

/** Décrypte la réponse ; tolère les erreurs réseau et les corps illisibles. */
function unpack(res: any): Reponse {
    try {
        const v = JSON.parse(Crypter.decrypt(res.data)).Value
        return { http: res.status, statut: v?.StatutCode ?? res.status, message: v?.Message || '', result: v?.Result }
    } catch {
        return { http: res.status, statut: res.status, message: '', result: null }
    }
}

const AXIOS_OPTS = { validateStatus: () => true, maxBodyLength: Infinity }

function postChemin(url: string, payload: any, token?: string): Promise<Reponse> {
    return axios.post(env.API_BASE_URL + url,
        payload === '' ? '' : Crypter.encrypt(typeof payload === 'string' ? payload : JSON.stringify(payload)),
        {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            ...AXIOS_OPTS,
        })
        .then(unpack)
        .catch((e) => ({ http: e.response?.status ?? 0, statut: 0, message: e.message || 'Serveur injoignable', result: null }));
}

/** A2 — code d'enrôlement : { PairingCode, PairingExpire, IntervalleInterrogationSec }. */
export function pairingInit(): Promise<Reponse> {
    return postChemin(datas.API_LINKs.Ecran.PairingInit, '')
}

/** A2 — 202 en attente ; 200 → Result.CleEcran ; 404 → code périmé. */
export function pairingComplete(pairingCode: string): Promise<Reponse> {
    return postChemin(datas.API_LINKs.Ecran.PairingComplete, { PairingCode: pairingCode })
}

/** A3 — { CleEcran } → { Token, TokenExpireLe, RenouvelerAvantSec, CodeFiliale, LibelleFiliale, Services[] }. */
export function demanderJeton(cleEcran: string): Promise<Reponse> {
    return postChemin(datas.API_LINKs.Ecran.Jeton, { CleEcran: cleEcran })
}

/** A4 — file complète de l'écran (Ecran, Services[], Affichage). */
export function chargerFile(token: string): Promise<Reponse> {
    return axios.get(env.API_BASE_URL + datas.API_LINKs.Ecran.File, {
        headers: { 'Accept': 'text/plain', 'Authorization': `Bearer ${token}` },
        ...AXIOS_OPTS,
    })
        .then(unpack)
        .catch((e) => ({ http: e.response?.status ?? 0, statut: 0, message: e.message || 'Serveur injoignable', result: null }));
}

/** A5 — signal de vie : Result.Commande éventuelle (« RECHARGER »). */
export function envoyerSignal(token: string, versionInstall: string): Promise<Reponse> {
    return postChemin(datas.API_LINKs.Ecran.Signal, { VersionInstall: versionInstall || '' }, token)
}

/* ---------- Déchiffrement non bloquant (pubs = plusieurs Mo) ---------- */

const b64 = (s: string) => Uint8Array.from(atob(s || ''), (c) => c.charCodeAt(0))

/**
 * AES-CBC identique à Crypter mais via WebCrypto : le décodage se fait hors
 * thread principal (pas de gel de l'affichage des tickets). Repli sur le
 * déchiffrement synchrone si WebCrypto est indisponible (http non sécurisé).
 */
async function decryptAsync(ciphertext: string): Promise<string> {
    try {
        if (!crypto?.subtle || !env.AES_KEY || !env.AES_IV) return Crypter.decrypt(ciphertext)
        const key = await crypto.subtle.importKey('raw', b64(env.AES_KEY), 'AES-CBC', false, ['decrypt'])
        const clair = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: b64(env.AES_IV) as any }, key, b64(ciphertext) as any)
        return new TextDecoder().decode(clair)
    } catch {
        return Crypter.decrypt(ciphertext)
    }
}

/** Version asynchrone de unpack pour les grosses réponses. */
async function unpackAsync(res: any): Promise<Reponse> {
    try {
        const v = JSON.parse(await decryptAsync(res.data)).Value
        return { http: res.status, statut: v?.StatutCode ?? res.status, message: v?.Message || '', result: v?.Result }
    } catch {
        return { http: res.status, statut: res.status, message: '', result: null }
    }
}

/**
 * A6 — publicités : Result.Publicites[].Fichiers = TABLEAU de data URL
 * (« data:image/… », « data:video/… »). Appelée au démarrage et sur
 * l'événement SignalR PubsUpdated — jamais en boucle (réponse lourde).
 */
export function chargerPubs(token: string): Promise<Reponse> {
    return axios.get(env.API_BASE_URL + datas.API_LINKs.Ecran.Pubs, {
        headers: { 'Accept': 'text/plain', 'Authorization': `Bearer ${token}` },
        ...AXIOS_OPTS,
    })
        .then(unpackAsync)
        .catch((e) => ({ http: e.response?.status ?? 0, statut: 0, message: e.message || 'Serveur injoignable', result: null }));
}
